import React from 'react';
import classNames from 'classnames';
import uniqid from 'uniqid';

type ToggleSize = 'sm' | 'md';

interface SizeConfig {
  track: string;
  knob: string;
}

const sizeConfig: Record<ToggleSize, SizeConfig> = {
  sm: {
    track: 'w-7 h-4',
    knob: 'after:h-3 after:w-3 after:top-[2px]',
  },
  md: {
    track: 'w-11 h-6',
    knob: 'after:h-5 after:w-5 after:top-[1.9px]',
  },
};

interface ToggleProps {
  onToggleChange: (checked: boolean) => void;
  isToggleOn: boolean;
  toggleText?: string;
  disabled?: boolean;
  size?: ToggleSize;
  labelPosition?: 'left' | 'right';
  'data-testid'?: string;
}

const Toggle = ({
  onToggleChange,
  isToggleOn,
  toggleText,
  disabled = false,
  size = 'md',
  labelPosition = 'left',
  'data-testid': dataTestId = 'toggle',
}: ToggleProps): React.JSX.Element => {
  const id = `toggle-${uniqid()}`;
  const { track, knob } = sizeConfig[size] || sizeConfig.md;

  const label = toggleText && (
    <span
      className={classNames({
        'mr-2': labelPosition === 'left',
        'ml-2': labelPosition === 'right',
      })}
    >
      {toggleText}
    </span>
  );

  return (
    <div
      className={classNames('flex items-center', { 'opacity-50': disabled })}
    >
      {labelPosition === 'left' && label}
      <label
        htmlFor={id}
        className={classNames('relative inline-flex items-center', {
          'cursor-pointer': !disabled,
        })}
      >
        <input
          id={id}
          type="checkbox"
          checked={isToggleOn}
          onChange={(e) => {
            if (!disabled) {
              onToggleChange(e.target.checked);
            }
          }}
          disabled={disabled}
          className="sr-only peer"
          aria-label={toggleText || 'Toggle'}
          data-testid={dataTestId}
        />
        <div
          className={classNames(
            'peer rounded-full bg-gray-300 dark:bg-gray-300',
            'peer-checked:after:translate-x-full',
            'after:absolute after:left-[2px] after:bg-white after:rounded-full after:transition-all',
            'peer-focus-visible:outline peer-checked:bg-lime-400',
            track,
            knob
          )}
        ></div>
      </label>
      {labelPosition === 'right' && label}
    </div>
  );
};

export default Toggle;
